import type { ProducerNumber, V4FuneralHome } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import { OapProvider } from '@/data/oapProviders'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useRetryMethods } from '@/services/retryUtils'
import type { SubmissionService } from '@/services/types'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import type { InmanEnrollRequest, InmanPaymentDetails } from '@/types'
import { captureException } from '@sentry/vue'
import type { AxiosRequestConfig } from 'axios'
import localforageLib from 'localforage'

const localInmanSubmissions = localforageLib.createInstance({
	name: 'request-queue',
	storeName: 'inmanSubmissions',
})

const inmanSubmissionsQueueConfig = {
	axiosInstance: purchaseApiAxiosInstance,
	requestBackgroundSync: async (tag: string) => {
		await windowRequestQueueConfig.requestBackgroundSync('submission-queue')
	},
}

const inmanSubmissionRequestQueue = useRequestQueue(
	'inmanSubmissions',
	inmanSubmissionsQueueConfig
)

// This getter exposes the sepio request queue to submission store
export const getQueue = () => {
	return inmanSubmissionRequestQueue
}

// This method checks whether OAP should submit
// if so, it creates the inman enrollment request and adds it to the queue
export const submitRequest = async (plan: Plan) => {
	// skip sending OAP if they used the offline form
	if (!shouldSubmit(plan)) {
		return
	}

	const sessionStore = useSessionStore()
	const funeralHome = sessionStore.activeFh as V4FuneralHome

	const data = mapOapSubmission(plan, funeralHome)

	const inmanRequestConfig: AxiosRequestConfig = {
		url: '/v1/oap/inman/enroll',
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		data,
	}

	try {
		await inmanSubmissionRequestQueue.addRequest(plan.id, inmanRequestConfig)
	} catch (error: any) {
		// only log to sentry, executeRequest takes care of updating appropriate status in the localForage
		captureException(error, {
			extra: {
				message: 'Error submitting enrollment for Inman.',
				response: error.response?.data,
			},
		})
	}
}

const mapOapSubmission = (
	plan: Plan,
	funeralHome: V4FuneralHome
): InmanEnrollRequest => {
	const authStore = useAuthStore()
	const salesperson = authStore?.user?.salesContact
	const salesforceContactAfpId = authStore.getActiveSalesContactId()!
	const salespersonInmanId =
		salesperson?.producerNumbers?.data.find(
			(item: ProducerNumber) => item.vendor === 'Inman'
		)?.producerNumber || ''

	const oapProvider = useOapPlanProvider(plan.oapPayment.provider)

	const paymentPlanDetails = plan.oapPayment
		.paymentPlan as InmanPaymentDetails | null

	const genderMap: Record<string, string> = {
		male: 'Male',
		female: 'Female',
		nonBinary: 'Non-Binary',
		other: 'Other',
	}
	const convertedGender = genderMap[plan.gender] ?? 'Other'

	const selectPlan = oapProvider.getPlanById(plan.oapPayment.paymentPlanId)

	return {
		planId: plan.id,
		salesforceContactAfpId,
		purchasePageId: selectPlan?.pricePointId ?? '',
		salesperson: {
			id: salespersonInmanId,
			firstName: salesperson?.firstname,
			lastName: salesperson?.lastname,
			email: salesperson?.email,
			additionalData: {
				'Funeral Home ID': funeralHome.id,
				'Funeral Home Name': funeralHome.name,
			},
		},
		participant: {
			firstName: plan.firstName,
			middleName: plan.middleName === '' ? undefined : plan.middleName,
			lastName: plan.lastName,
			gender: convertedGender,
			birthDate: plan.birthDate,
			email: plan.email ?? undefined,
			phone: plan.phone,
		},
		address: {
			address: plan.address,
			city: plan.city,
			state: plan.state,
			zip: plan.zip,
		},
		paymentPlanMonths: selectPlan?.paymentCount ?? 1,
		paymentPlanDayOfMonth: paymentPlanDetails?.paymentDayOfMonth,
	}
}

const shouldSubmit = (plan: Plan): boolean => {
	return (
		plan.completedSteps.includes('OAP Enrollment') &&
		plan.oapPayment.provider === OapProvider.INMAN
	)
}

export const inmanService: SubmissionService = {
	getQueue,
	submitRequest,
	// Validation is not needed for Inman because the data is being retrieved from the logged in User and the plan details.
	validateRequest: () => true,
	serviceName: 'Inman',
	storeName: 'inmanSubmissions',
	shouldSubmit,
	...useRetryMethods(
		inmanSubmissionRequestQueue,
		localInmanSubmissions,
		'Inman'
	),
}
